//AdvancedSearch.tsx ==============================================================
import "../Util/Prototypes";

import * as AdvancedSearch from "./AdvancedSearch";
import * as AutoCompleteAdvancedMUI from "./AutoCompleteAdvancedMUI";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GetSearchResults from "./GetSearchResults";
import * as NoRecordsFound from "./NoRecordsFound";
import * as Search from "./Search";
import * as SelectionMode from "./SelectionMode";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Button, ButtonGroup, Icon, IconButton, Paper, TextField, Typography } from "@mui/material";
import { blueGrey, red } from '@mui/material/colors';

//ESRI javascript API.
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

import React, { RefObject } from "react";

const toolName: string = "Advanced Search";

export let layerName: string;  // Advanced search selectable layer name;
export let advancedSQL: string;

let focusMeIndex: number;
let fieldNames: string[];
let aliasNames: string[];

interface iQuery {
   leftParen: string;
   fieldName: string;
   aliasName: string;
   dataType: string;
   operatorLabel: string;
   operatorValue: string;
   value: string;
   rightParen: string;
   andOr: string;
   autoSuggestID: string;
   autoCompleteRef: RefObject<HTMLElement>;
   focusMe: boolean;
}
let queries: iQuery[] = []; // Array of all sql query rows.

interface iField {
   fieldName: string;
   aliasName: string;
   dataType: string;
}
let fields: iField[] = []; //Array of all fields and data types.

const textOperatorLabels: string[] = [
   "is equal to",
   "is not equal to",
   "begins with",
   "does not begin with",
   "ends with",
   "does not end with",
   "includes the value(s)",
   "does not include the value(s)",
   "contains the text",
   "does not contain the text",
   "contains all the words",
   "does not contain all the words"];
//"is null",
//"is not null"];

const textOperatorValues: string[] = [
   "=",
   "<>",
   "begins with",
   "does not begin with",
   "ends with",
   "does not end with",
   "in (",
   "not in (",
   "contains the text",
   "does not contain the text",
   "contains all the words",
   "does not contain all the words"];
//"is NULL",
//"is not NULL"];

const numericOperatorLabels: string[] = [
   "is equal to",
   "is not equal to",
   "is greater than",
   "is greater than or equal to",
   "is less than",
   "is less than or equal to",
   "includes the value(s)",
   "does not include the value(s)"];
//"is null",
//"is not null"];

const numericOperatorValues: string[] = [
   "=",
   "<>",
   ">",
   ">=",
   "<",
   "<=",
   "in (",
   "not in ("];
//"is NULL",
//"is not NULL"];

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of AdvancedSearch Render");

   //Initialize.
   if (typeof AdvancedSearch.layerName === "undefined") {
      layerName = Search.activeLayer;
      getFieldNames();
   }

   //Wire up component for each time render is called, but not on refresh.
   const [initComponent, setState] = React.useState(() => {
      Search.setActiveSearchTool(toolName);
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {

      //Component will unmount.
      return () => {
      };
   }, []);

   //Component did update.
   React.useEffect(() => {
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Get field names and data types.
   function getFieldNames() {

      //Initialize query task.
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(AdvancedSearch.layerName);
      if (!laySpec) {
         Util.errMsg("Layer setting not found for " + AdvancedSearch.layerName);
         return;
      }

      //Initialize query.
      const query = new Query({
         returnGeometry: false,
         outFields: ["*"],
         start: 0,
         num: 1,
      });

      //Execute query.
      const url: string = Util_App.getLayerURL(laySpec);
      QueryTask.executeQueryJSON(url, query).then(queryTask_success).catch(queryTask_failed);
   }

   //Query task completed.
   function queryTask_success(fs: __esri.FeatureSet) {
      const laySpec: Settings.iLayer = Util_App.getLayerSpec(AdvancedSearch.layerName);

      //Build fields array.
      fields = [];

      for (const field of fs.fields) {
         const fieldName: string = field.name;
         const alias: Util_App.iAlias = Util_App.parseAliasName(field.alias);
         const aliasName: string = alias.name;
         let dataType: string = field.type;

         if (dataType === "oid") {
            dataType = "integer";
         }

         if (!laySpec.omitAdvancedSearchFields.Contains(fieldName) && fieldName !== "Shape" && fieldName !== "GlobalID") {
            if (Settings.security.accessLevel >= alias.accessLevel) {
               fields.Add({ fieldName: fieldName, aliasName: aliasName, dataType: dataType });
            }
         }
      }

      fields.sort(Util.sortArray("aliasName"));

      //Build field and alias names arrays.
      fieldNames = [];
      for (const field of fields) {
         fieldNames.Add(field.fieldName);
      }
      aliasNames = [];
      for (const field of fields) {
         aliasNames.Add(field.aliasName);
      }
      //Util.errMsg("Fields", fields);

      //Initialize query array because layer name has changed.
      queries = [getDefaultQueryRow()];
      focusMeIndex = 0;
      refresh();
   }

   //Query task failed.
   function queryTask_failed(error: Error) {
      Util.errMsg("Advanced Search - QueryTask failed", error);
   }

   //Get default query row data.
   function getDefaultQueryRow(): iQuery {
      const id: string = "id" + Math.random().toString(36).slice(2);
      let ii: number = 0;

      for (let i = 0; i < fields.length; i++) {
         if (fields[i].dataType === "string") {
            ii = i; //Set default field index to first string field.
            break;
         }
      }
      for (let i = 0; i < fields.length; i++) {
         if (fields[i].dataType === "string" && fields[i].aliasName.Contains("Owner")) {
            ii = i; //Set default field index to owner name field.
            break;
         }
      }

      let operatorLabel: string = "is equal to";
      let operatorValue: string = "=";
      if (fields[ii].dataType === "string") {
         operatorLabel = "contains all the words";
         operatorValue = "contains all the words";
      }

      const query: iQuery = {
         leftParen: "(", fieldName: fields[ii].fieldName, aliasName: fields[ii].aliasName, dataType: fields[ii].dataType,
         operatorLabel: operatorLabel, operatorValue: operatorValue, value: "", rightParen: ")", andOr: "And",
         autoSuggestID: id, autoCompleteRef: null, focusMe: false,
      };

      return query;
   }

   //Get alias name by field name.
   function getAliasNameByFieldName(fieldName: string): string {
      for (const field of fields) {
         if (field.fieldName === fieldName) {
            return field.aliasName;
         }
      }
   }

   //Get field data type by field name.
   function getDataTypeByFieldName(fieldName: string): string {
      for (const field of fields) {
         if (field.fieldName === fieldName) {
            return field.dataType;
         }
      }
   }

   //Selectable layer changed.
   function selectableLayerChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      layerName = event.target.value;
      Util_App.setActiveLayer(AdvancedSearch.layerName);
      getFieldNames();
   }

   //Field name changed.
   function fieldNameChanged(i: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      queries[i].fieldName = event.target.value;
      queries[i].aliasName = getAliasNameByFieldName(queries[i].fieldName);
      queries[i].dataType = getDataTypeByFieldName(queries[i].fieldName);

      let operatorLabels: string[] = numericOperatorLabels;
      let operatorValues: string[] = numericOperatorValues;

      if (queries[i].dataType === "string") {
         operatorLabels = textOperatorLabels;
         operatorValues = textOperatorValues;
      }

      if (!operatorValues.Contains(queries[i].operatorValue)) {
         queries[i].operatorLabel = operatorLabels[0];
         queries[i].operatorValue = operatorValues[0];
      }

      focusMeIndex = i;
      refresh();
   }

   //Operator changed.
   function operatorChanged(i: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      queries[i].operatorValue = event.target.value;
      queries[i].operatorLabel = getOperatorLabelByOperatorValue(i);
      focusMeIndex = i;
      refresh();
   }

   //Get operator label by operator value.
   function getOperatorLabelByOperatorValue(i: number): string {
      const operatorValue: string = queries[i].operatorValue;
      let j: number = 0;

      if (queries[i].dataType === "string") {
         for (const opValue of textOperatorValues) {
            if (opValue === operatorValue) {
               return textOperatorLabels[j];
            }
            j++;
         }

      } else {
         for (const opValue of numericOperatorValues) {
            if (opValue === operatorValue) {
               return numericOperatorLabels[j];
            }
            j++;
         }
      }
   }

   //And/Or click event.
   function handleAndOr(i: number) {
      if (queries[i].andOr === "And") {
         queries[i].andOr = "Or";
      } else {
         queries[i].andOr = "And";
      }
      refresh();
   }

   //Left paren click event.
   function handleLeftParen(i: number) {
      if (queries[i].leftParen === "") {
         queries[i].leftParen = "(";
      } else {
         queries[i].leftParen = "";
      }
      refresh();
   }

   //Right paren click event.
   function handleRightParen(i: number) {
      if (queries[i].rightParen === "") {
         queries[i].rightParen = ")";
      } else {
         queries[i].rightParen = "";
      }
      refresh();
   }

   //Clear query row clicked.
   function handleClear(i: number) {
      queries.RemoveAt(i);
      focusMeIndex = i;
      if (focusMeIndex >= queries.length) {
         focusMeIndex--;
      }
      refresh();
   }

   //Add a new query row.
   function addQuery() {
      queries.Add(getDefaultQueryRow());
      focusMeIndex = queries.length - 1;
      refresh();
   }

   //Autocomplete value changed event.
   function autoCompleteValueChanged(value: string, i: number) {
      //Util.errMsg("Called back value: " + i + ") " + value);
      queries[i].value = value;
   }

   //Autocomplete enter key pressed or suggestion was selected.
   function autoCompleteSelected(selectedValue, i: number): void {
      queries[i].value = selectedValue as string;
      handleSearch();
   }

   //Search button click event.
   function handleSearch() {

      //QC the parenthesis.
      let isValidParen: boolean = true;

      if (queries.length > 2) {
         let parenStr: string = "";
         for (const q of queries) {
            parenStr += q.leftParen;
            parenStr += q.rightParen;
         }
         isValidParen = Util_App.isValidQueryParenthesis(parenStr);
      }

      //Perform advanced search.
      if (isValidParen) {
         buildAdvancedSQL();
         GetSearchResults.init();

      } else {
         NoRecordsFound.setMsg("Mismatched parenthesis");
      }
   }

   //Build advanced sql string.
   function buildAdvancedSQL() {
      let sql: string = "";

      for (const q of queries) {
         if (sql !== "") {
            sql += " " + q.andOr + " ";
         }

         let leftParen: string = q.leftParen;
         let rightParen: string = q.rightParen;
         if (queries.length < 3) {
            leftParen = "";
            rightParen = "";
         }

         //Get coded value.
         for (const codedValue of Settings.codedValues) {
            if (layerName === codedValue.layerName && q.fieldName === codedValue.fieldName) {
               for (const value of codedValue.values) {
                  if (q.value === value.name) {
                     q.value = value.code.toString();
                     break;
                  }
               }
            }
         }

         //Build query clause.
         const queryClause: string = Util_App.buildQueryClause(q.fieldName, q.operatorValue, q.value, q.dataType);
         sql += leftParen + queryClause + rightParen;
      }

      advancedSQL = sql;
      Util.logMsg("advancedSQL: " + sql);
   }

   //Build a query row.
   function buildQueryRow(sql: iQuery, i: number): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      let firstRow: boolean = true;
      if (i > 0) {
         firstRow = false;
      }

      let twoRows: boolean = false;
      if (queries.length > 1) {
         twoRows = true;
      }

      let threeRows: boolean = false;
      if (queries.length > 2) {
         threeRows = true;
      }

      const dataType: string = queries[i].dataType;
      let operatorLabels: string[] = numericOperatorLabels;
      let operatorValues: string[] = numericOperatorValues;

      if (dataType === "string") {
         operatorLabels = textOperatorLabels;
         operatorValues = textOperatorValues;
      }

      const operatorLabel: string = queries[i].operatorLabel;
      const operatorValue: string = queries[i].operatorValue;

      //Set focus.
      queries[i].focusMe = false;
      if (i === focusMeIndex) {
         queries[i].focusMe = true;
      }

      //Set ajax data for autocomplete.
      const ajaxData: object = { fieldName: queries[i].fieldName, operator: operatorValue, dataType: dataType };

      //Styles.
      const styles: Default.iStyles = {
         paper: { backgroundColor: blueGrey[50], marginTop: 12, padding: 4, paddingTop: 0 },
         button: { width: 47 },
         clearButton: { color: red[700] },
      };

      let firstRowClass: string = "app-flex-row-start";
      if (twoRows) {
         firstRowClass = "app-flex-row-between";
      }

      //Util.errMsg("buildQueryRow: " + i + ") " + queries[i].value);

      jsx.push(
         <Paper key={Util.uid()} style={styles.paper}>

            <div className={firstRowClass}>
               {firstRow &&
                  <div className="app-flex-row-start">
                     <Typography style={{ marginRight: 12 }} variant="body1">
                        Where
                     </Typography>

                     <ButtonGroup size="small" onClick={() => handleLeftParen(i)} title="Toggle opening parentheses" aria-label="Left paren">
                        {(sql.leftParen === "") && threeRows &&
                           <Button style={{ marginRight: 7 }}>&nbsp;</Button>
                        }
                        {(sql.leftParen === "(") && threeRows &&
                           <Button style={{ marginRight: 7 }}>(</Button>
                        }
                     </ButtonGroup>
                  </div>
               }

               {!firstRow &&
                  <ButtonGroup size="small">
                     {(sql.andOr === "And") &&
                        <Button style={styles.button} onClick={() => handleAndOr(i)} title='Toggle "And/Or" operator' aria-label="Toggle">And</Button>
                     }
                     {(sql.andOr === "Or") &&
                        <Button style={styles.button} onClick={() => handleAndOr(i)} title='Toggle "And/Or" operator' aria-label="Toggle">Or</Button>
                     }
                     {(sql.leftParen === "") && threeRows &&
                        <Button onClick={() => handleLeftParen(i)} title="Toggle opening parentheses" aria-label="Toggle">&nbsp;</Button>
                     }
                     {(sql.leftParen === "(") && threeRows &&
                        <Button onClick={() => handleLeftParen(i)} title="Toggle opening parentheses" aria-label="Toggle">(</Button>
                     }
                  </ButtonGroup>
               }

               <TextField select style={{ marginTop: 7 }}
                  label="Database field"
                  title="Database field name"
                  inputProps={{ id: "db-field-name" + i.toString() }}
                  InputLabelProps={{ htmlFor: "db-field-name" + i.toString() }}
                  value={queries[i].fieldName}
                  onChange={(event) => fieldNameChanged(i, event)}
               >
                  {Util_MUI.buildSelectMenuItems(aliasNames, fieldNames)}
               </TextField>

               {twoRows &&
                  <IconButton style={styles.clearButton} color="primary" onClick={() => handleClear(i)} title="Remove this query clause" aria-label="Remove" >
                     <Icon fontSize="small">close</Icon>
                  </IconButton>
               }
            </div>

            {/* Second Row. */}
            <div className="app-flex-row-between" style={{ marginTop: 7 }}>

               <TextField select style={{ width: 120, marginRight: 7 }}
                  label="Operator"
                  title={operatorLabel}
                  inputProps={{ id: "operator-id" + i.toString() }}
                  InputLabelProps={{ htmlFor: "operator-id" + i.toString() }}
                  value={operatorValue}
                  onChange={(event) => operatorChanged(i, event)}
               >
                  {Util_MUI.buildSelectMenuItems(operatorLabels, operatorValues)}
               </TextField>

               <AutoCompleteAdvancedMUI.Render
                  initialValue={queries[i].value}
                  width={140}
                  backgroundColor="White"
                  highlightColor={Settings.hoverColor}
                  toMixedCase={true}
                  tooltip="Enter a value or ? for suggestions"
                  focusMe={queries[i].focusMe}
                  ajaxData={ajaxData}
                  onValueChanged={(label) => autoCompleteValueChanged(label, i)}
                  onSelected={(value) => autoCompleteSelected(value, i)}
               >
                  value
               </AutoCompleteAdvancedMUI.Render>

               <ButtonGroup size="small" onClick={() => handleRightParen(i)} title="Toggle closing parentheses" aria-label="Right paren">
                  {(sql.rightParen === "") && threeRows &&
                     <Button>&nbsp;</Button>
                  }
                  {(sql.rightParen === ")") && threeRows &&
                     <Button>)</Button>
                  }
               </ButtonGroup>
            </div>
         </Paper>,
      ); //end jsx.push.

      return jsx;
   }

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   //Util.errMsg("Rendering Advanced Search: ", queries);

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <div style={{ marginLeft: 7, marginRight: 50, marginBottom: 9 }}>

               <TextField select fullWidth style={{ marginTop: 7 }}
                  label="Select from"
                  title="The map layer to select from"
                  inputProps={{ id: "query-map-layers" }}
                  InputLabelProps={{ htmlFor: "query-map-layers" }}
                  value={AdvancedSearch.layerName}
                  onChange={selectableLayerChanged}
               >
                  {Util_MUI.buildSelectMenuItems(Settings.selectableLayers)}
               </TextField>
            </div>

            {/* Build all query clauses. */}
            {queries.map(function (sql: iQuery, i: number) {
               return (buildQueryRow(sql, i));
            })}

            {/* Add and Search button. */}
            <br />
            <div className="app-flex-row-around">
               <Button variant="outlined" size="medium" color="info" onClick={addQuery} title="Add new query clause" aria-label="Add">
                  <Icon color="action">add</Icon>
                  <span style={{ marginLeft: 6 }}></span>
                  Add
               </Button>

               <Button variant="outlined" size="medium" color="info" onClick={handleSearch} title="Search" aria-label="Search">
                  <Icon color="action">search</Icon>
                  <span style={{ marginLeft: 7 }}></span>
                  Search
               </Button>
            </div>

            <br />
            <NoRecordsFound.Render />
            <SelectionMode.Render tool="AdvancedSearch" />
         </div>
      </Paper>

   ); //end return.

} //end function.
